'use client';

import * as React from 'react';

type PodupTheme = 'dark' | 'light';

const ThemeContext = React.createContext<{
  theme: PodupTheme;
  setTheme: (t: PodupTheme) => void;
} | null>(null);

const STORAGE_KEY = 'podup-theme';

export function ThemeProvider(props: { children: React.ReactNode }) {
  // Server always renders 'dark' (matches the static attribute already set
  // in layout.tsx) - this reads the real saved preference once mounted,
  // avoiding SSR/localStorage mismatch complexity. Accepts a brief flash
  // for the rare case someone previously chose light mode, which is an
  // acceptable tradeoff given dark is explicitly the intended default.
  const [theme, setThemeState] = React.useState<PodupTheme>('dark');

  React.useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved === 'light' || saved === 'dark') {
        setThemeState(saved);
      }
    } catch {
      // localStorage unavailable (e.g. private browsing) - just stay dark.
    }
  }, []);

  React.useEffect(() => {
    document.body.setAttribute('data-podup-theme', theme);
  }, [theme]);

  const setTheme = React.useCallback((t: PodupTheme) => {
    setThemeState(t);
    try {
      localStorage.setItem(STORAGE_KEY, t);
    } catch {
      // Best-effort only.
    }
  }, []);

  return <ThemeContext.Provider value={{ theme, setTheme }}>{props.children}</ThemeContext.Provider>;
}

export function usePodupTheme() {
  const ctx = React.useContext(ThemeContext);
  if (!ctx) {
    throw new Error('usePodupTheme must be used within a ThemeProvider');
  }
  return ctx;
}
